import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, useSpring, useMotionValue } from 'framer-motion';
import { Quote } from 'lucide-react';
import Globe from 'react-globe.gl';

const testimonials = [
  {
    text: "I met this guy from BITS Goa on a 1-1 Voice call. What started as an anonymous chat ended up with us being best friends. When you're anonymous, you have no fear, so you can just be real and find people that actually match your vibe.",
    author: "Anonymous, IIT Delhi",
  },
  {
    text: "Bruh, my friend sent a Custom Room link in our unofficial group, and it filled up instantly. No one knew who was who! We planned a mass bunk, people confessed feelings... the mystery made it super fun!",
    author: "Anonymous, UCL",
  },
  {
    text: "Honestly, the 1-1 and 4-4 rooms are a vibe. Other platforms are full of creeps, but Spark’s college-only exclusivity makes it completely different. Zero pressure, just pure connection.",
    author: "Anonymous, Delhi University",
  },
];
const universities = [
  'Delhi University',
  'IIT Bombay',
  'Harvard University',
  'Stanford University',
  'MIT',
  'UCLA',
  'Yale University',
  'Oxford University',
  'Cambridge University',
  'Imperial College London',
  'University of Toronto',
  'McGill University',
  'University of Bologna',
  'Sapienza University of Rome',
  'University of Tokyo',
  'ETH Zurich',
  'National University of Singapore',
  'University of Melbourne',
  'University of Cape Town',
  'Sorbonne University',
];

const InfiniteMarquee = ({ items, reverse = false, speed = 40, className }) => {
  return (
    <div className={`flex overflow-hidden whitespace-nowrap select-none w-full ${className}`}>
      <motion.div
        className="flex items-center gap-16 pr-16"
        animate={{ x: reverse ? ["-100%", "0%"] : ["0%", "-100%"] }}
        transition={{ duration: speed, ease: "linear", repeat: Infinity }}
      >
        {items.map((item, idx) => (
          <span key={idx} className="text-[100px] md:text-[150px] font-black uppercase text-white/[0.03] tracking-tighter mix-blend-overlay">
            {item} •
          </span>
        ))}
      </motion.div>
      <motion.div
        className="flex items-center gap-16 pr-16"
        animate={{ x: reverse ? ["-100%", "0%"] : ["0%", "-100%"] }}
        transition={{ duration: speed, ease: "linear", repeat: Infinity }}
      >
        {items.map((item, idx) => (
          <span key={idx} className="text-[100px] md:text-[150px] font-black uppercase text-white/[0.03] tracking-tighter mix-blend-overlay">
            {item} •
          </span>
        ))}
      </motion.div>
    </div>
  );
};

export const CommunitySection = () => {
  const [places, setPlaces] = useState([]);
  const [arcs, setArcs] = useState([]);
  const globeRef = useRef();

  useEffect(() => {
    // Load cities data - simplified for top cities per continent
       const majorCities = [
      { lat: 40.7128, lng: -74.0060, continent: 'North America' }, // New York
      { lat: 51.5074, lng: -0.1278, continent: 'Europe' }, // London
      { lat: 35.6762, lng: 139.6503, continent: 'Asia' }, // Tokyo
      { lat: -33.8688, lng: 151.2093, continent: 'Oceania' }, // Sydney
      { lat: -22.9068, lng: -43.1729, continent: 'South America' }, // Rio
      { lat: 30.0444, lng: 31.2357, continent: 'Africa' }, // Cairo
      { lat: 28.6139, lng: 77.2090, continent: 'Asia' }, // Delhi
      { lat: 19.0760, lng: 72.8777, continent: 'Asia' }, // Mumbai
      { lat: 34.0522, lng: -118.2437, continent: 'North America' }, // Los Angeles
      { lat: 41.8781, lng: -87.6298, continent: 'North America' }, // Chicago
      { lat: 48.8566, lng: 2.3522, continent: 'Europe' }, // Paris
      { lat: 52.5200, lng: 13.4050, continent: 'Europe' }, // Berlin
      { lat: 39.9042, lng: 116.4074, continent: 'Asia' }, // Beijing
      { lat: -34.6037, lng: -58.3816, continent: 'South America' }, // Buenos Aires
      { lat: -23.5505, lng: -46.6333, continent: 'South America' }, // Sao Paulo
      { lat: -33.9249, lng: 18.4241, continent: 'Africa' }, // Cape Town
      { lat: 6.5244, lng: 3.3792, continent: 'Africa' }, // Lagos
      { lat: -37.8136, lng: 144.9631, continent: 'Oceania' }, // Melbourne
      { lat: -27.4698, lng: 153.0251, continent: 'Oceania' }, // Brisbane
      { lat: 45.4215, lng: -75.6972, continent: 'North America' }, // Ottawa
      { lat: 55.7558, lng: 37.6173, continent: 'Europe' }, // Moscow
      { lat: 37.5665, lng: 126.9780, continent: 'Asia' }, // Seoul
      { lat: 4.7110, lng: -74.0721, continent: 'South America' }, // Bogota
      { lat: 31.2001, lng: 29.9187, continent: 'Africa' }, // Alexandria
      { lat: 37.7749, lng: -122.4194, continent: 'North America' }, // San Francisco, California
    ];

    setPlaces(majorCities.map(city => ({
      ...city,
      alt: 0.01, // Small altitude for points
    })));

    // Generate random arcs between different continents
    const connections = [];
    const seen = new Set();
    while (connections.length < 20) {
      const startIdx = Math.floor(Math.random() * majorCities.length);
      const endIdx = Math.floor(Math.random() * majorCities.length);
      const key = `${startIdx}-${endIdx}`;
      if (startIdx !== endIdx && majorCities[startIdx].continent !== majorCities[endIdx].continent && !seen.has(key)) {
        seen.add(key);
        connections.push({
          startLat: majorCities[startIdx].lat,
          startLng: majorCities[startIdx].lng,
          endLat: majorCities[endIdx].lat,
          endLng: majorCities[endIdx].lng,
          color: '#0BD9E5',
        });
      }
    }
    setArcs(connections);
  }, []);

  // Performance-optimized Parallax Setup (bypasses React re-renders)
  const sectionRef = useRef(null);
  
  // Directly bind cursor mapping to motion values so the React component 
  // doesn't re-render 60 times a second and reset the 3D globe arcs.
  const cursorX = useMotionValue(0);
  const cursorY = useMotionValue(0);

  const handleMouseMove = (e) => {
    if (!sectionRef.current) return;
    const { clientX, clientY } = e;
    const { left, top, width, height } = sectionRef.current.getBoundingClientRect();
    const x = (clientX - left) / width - 0.5;
    const y = (clientY - top) / height - 0.5;
    
    // Push directly to Framer Motion without firing a React re-render queue
    cursorX.set(x * 150); // Increased multiplier for more noticeable drag
    cursorY.set(y * 150);
  };

  const springConfig = { damping: 25, stiffness: 100 };
  const mouseX = useSpring(cursorX, springConfig);
  const mouseY = useSpring(cursorY, springConfig);

  // Subtle scroll parallax linking the avatar wrapper Y-axis to scroll depth
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  const scrollY = useTransform(scrollYProgress, [0, 1], [100, -100]);

  // Wide, expansive 24-Avatar layout filling the horizontal space
  const floatingAvatars = [
    // Core Inner Orbit
    { src: '/avatars/Blaze.png', size: '14', initial: { x: -180, y: -70 }, animate: { y: [-70, -90, -70], rotate: [0, 5, -5, 0] }, delay: 0 },
    { src: '/avatars/Nova.png', size: '16', initial: { x: 140, y: -50 }, animate: { y: [-50, -65, -50], x: [140, 150, 140], rotate: [0, -10, 0] }, delay: 1.5 },
    { src: '/avatars/Echo.png', size: '12', initial: { x: -140, y: 80 }, animate: { y: [80, 100, 80], rotate: [0, 15, -5, 0] }, delay: 0.8 },
    { src: '/avatars/Pulse.png', size: '20', initial: { x: 190, y: 60 }, animate: { y: [60, 45, 60], x: [190, 175, 190], rotate: [0, -5, 5, 0] }, delay: 2.2 },
    { src: '/avatars/Frost.png', size: '14', initial: { x: 0, y: -130 }, animate: { x: [0, 20, -10, 0], y: [-130, -145, -130] }, delay: 1.1 },
    { src: '/avatars/Vortex.png', size: '12', initial: { x: 0, y: 110 }, animate: { x: [0, -20, 10, 0], y: [110, 125, 110] }, delay: 2.8 },
    
    // Mid Orbit
    { src: '/avatars/Zenith.png', size: '10', initial: { x: -250, y: 10 }, animate: { y: [10, 30, 10], rotate: [0, 10, 0] }, delay: 0.4 },
    { src: '/avatars/Sky.png', size: '14', initial: { x: 250, y: -10 }, animate: { y: [-10, -30, -10], rotate: [0, -5, 5, 0] }, delay: 3.1 },
    { src: '/avatars/Craze.png', size: '16', initial: { x: -280, y: -100 }, animate: { y: [-100, -120, -100], x: [-280, -260, -280], rotate: [0, -5, 5, 0] }, delay: 0.7 },
    { src: '/avatars/Volt.png', size: '12', initial: { x: 300, y: 100 }, animate: { y: [100, 80, 100], x: [300, 320, 300], rotate: [0, 10, -5, 0] }, delay: 1.9 },
    { src: '/avatars/Dusk.png', size: '14', initial: { x: -320, y: 120 }, animate: { y: [120, 100, 120], rotate: [0, -15, 0] }, delay: 2.5 },
    { src: '/avatars/Raze.png', size: '16', initial: { x: 280, y: -110 }, animate: { y: [-110, -90, -110], rotate: [0, 8, -8, 0] }, delay: 0.2 },
    
    // Far Left / Wide Orbit
    { src: '/avatars/Chill.png', size: '16', initial: { x: -400, y: -40 }, animate: { y: [-40, -60, -40], rotate: [0, -10, 0] }, delay: 1.2 },
    { src: '/avatars/Hex.png', size: '14', initial: { x: -450, y: -150 }, animate: { y: [-150, -135, -150], x: [-450, -460, -450], rotate: [0, 5, -5, 0] }, delay: 2.4 },
    { src: '/avatars/Rebel.png', size: '20', initial: { x: -500, y: 60 }, animate: { y: [60, 45, 60], rotate: [0, -5, 5, 0] }, delay: 0.5 },
    { src: '/avatars/Wraith.png', size: '12', initial: { x: -380, y: 180 }, animate: { y: [180, 195, 180], x: [-380, -360, -380] }, delay: 3.3 },
    { src: '/avatars/Shade.png', size: '10', initial: { x: -550, y: -90 }, animate: { y: [-90, -110, -90], rotate: [0, 15, 0] }, delay: 1.8 },
    { src: '/avatars/Mystic.png', size: '16', initial: { x: -480, y: 140 }, animate: { x: [-480, -500, -480], rotate: [0, -10, 10, 0] }, delay: 0.9 },
    
    // Far Right / Wide Orbit
    { src: '/avatars/Storm.png', size: '14', initial: { x: 380, y: 140 }, animate: { y: [140, 120, 140], rotate: [0, -10, 0] }, delay: 1.6 },
    { src: '/avatars/Faze.png', size: '16', initial: { x: 440, y: -60 }, animate: { y: [-60, -45, -60], x: [440, 450, 440], rotate: [0, 5, -5, 0] }, delay: 2.1 },
    { src: '/avatars/Jolt.png', size: '20', initial: { x: 480, y: 40 }, animate: { y: [40, 55, 40], rotate: [0, -5, 5, 0] }, delay: 0.3 },
    { src: '/avatars/Drift.png', size: '12', initial: { x: 360, y: -160 }, animate: { y: [-160, -175, -160], x: [360, 340, 360] }, delay: 2.9 },
    { src: '/avatars/Glow.png', size: '10', initial: { x: 530, y: -130 }, animate: { y: [-130, -110, -130], rotate: [0, 15, 0] }, delay: 1.4 },
    { src: '/avatars/Vibe.png', size: '14', initial: { x: 460, y: 180 }, animate: { x: [460, 480, 460], rotate: [0, -10, 10, 0] }, delay: 0.6 },
  ];

  return (
    <section className="relative py-20 px-4" ref={sectionRef} onMouseMove={handleMouseMove}>
      <div className="max-w-7xl mx-auto">
        <motion.h2
          className="text-4xl md:text-5xl font-medium tracking-tight text-sp-text text-center mb-4"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          
        >
          Join the Community
        </motion.h2>
        <motion.p
          className="text-sp-text-secondary text-center mb-16 text-lg max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Thousands of students are already sparking connections on campus
        </motion.p>
        {/* Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              className="glass-card p-8 rounded-3xl"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -8, rotateY: 5 }}
            >
              <Quote className="w-8 h-8 text-sp-cyan mb-4 opacity-60" />
              <p className="text-sp-text text-lg mb-4 leading-relaxed">
                "{testimonial.text}"
              </p>
              <p className="text-sp-text-secondary text-sm font-medium">
                {testimonial.author}
              </p>
            </motion.div>
          ))}
        </div>
        {/* Universities carousel */}
        <motion.div
          className="relative overflow-hidden py-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-6">
            <h3 className="text-2xl font-medium tracking-tight text-sp-text mb-2">Active on campuses across the globe</h3>
          </div>
          <div className="relative overflow-hidden">
            <motion.div
              className="flex gap-6 whitespace-nowrap"
              animate={{ x: ['0%', '-50%'] }}
              transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
            >
              {[...universities, ...universities].map((university, index) => (
                <div
                  key={index}
                  className="glass-card px-6 py-3 rounded-full inline-block"
                >
                  <span className="text-sp-cyan font-medium">{university}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </motion.div>
        {/* 3D Globe with Infinite Marquee Background */}
        <div className="relative h-[40rem] my-8 flex justify-center items-center w-[100vw] left-1/2 -translate-x-1/2"> 
          
          {/* Background Infinite Marquees */}
          <div className="absolute inset-0 z-0 flex flex-col justify-center gap-8 pointer-events-none opacity-80">
            <InfiniteMarquee items={universities} speed={80} />
            <InfiniteMarquee items={universities} reverse={true} speed={60} />
            <InfiniteMarquee items={universities} speed={70} />
          </div>

          <div className="absolute inset-0 z-0 flex justify-center items-center pointer-events-none">
            <motion.div 
              className="w-[500px] h-[500px] rounded-full border border-[rgba(11,217,229,0.1)] border-t-[rgba(11,217,229,0.5)] border-b-[rgba(11,217,229,0.5)]"
              animate={{ rotateZ: 360, rotateX: 60 }}
              initial={{ rotateX: 60 }}
              transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
            />
          </div>

          <Globe
            ref={globeRef}
            globeImageUrl="//unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
            backgroundColor="rgba(0,0,0,0)"
            showAtmosphere={false}
            onGlobeReady={() => {
              try {
                if (globeRef.current && globeRef.current.controls) {
                  globeRef.current.controls().enableZoom = false;
                  globeRef.current.controls().autoRotate = true;
                  globeRef.current.controls().autoRotateSpeed = 2.0;
                }
              } catch (e) {
                // console.log("Globe controls not ready");
              }
            }}
            pointsData={places}
            pointLat="lat"
            pointLng="lng"
            pointAltitude="alt"
            pointColor={() => '#0BD9E5'}
            pointRadius={0.5}
            arcsData={arcs}
            arcColor="color"
            arcDashLength={() => Math.random()}
            arcDashGap={() => Math.random()}
            arcDashAnimateTime={() => Math.random() * 4000 + 500}
            arcAltitudeAutoScale={0.4}
            width={450} // Adjust width and height to change globe size
            height={450}
          />
          
          {/* Floating Avatars Container */}
          <motion.div 
            className="absolute inset-0 z-10 pointer-events-none flex justify-center items-center"
            style={{ 
              x: mouseX, 
              // Combine mouse drag parallax with vertical scroll parallax
              y: useTransform(() => mouseY.get() + scrollY.get())
            }}
          >
            {floatingAvatars.map((avatar, idx) => (
              <motion.div
                key={idx}
                className={`absolute top-1/2 left-1/2 rounded-full border-2 border-white/10 glass-card bg-[#0a0a0a]/50 backdrop-blur-md overflow-hidden hidden md:block w-${avatar.size} h-${avatar.size}`}
                style={{
                  marginLeft: avatar.initial.x,
                  marginTop: avatar.initial.y,
                  boxShadow: '0 0 15px rgba(11, 217, 229, 0.15)'
                }}
                animate={avatar.animate}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: avatar.delay,
                }}
              >
                <img 
                  src={avatar.src} 
                  alt="Floating Avatar" 
                  className="w-full h-full object-cover scale-[1.2] opacity-90" 
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
        {/* Stats */}
        {/* <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {[
            { number: '5K+', label: 'Active Users' },
            { number: '100000+', label: 'Universities Listed' },
            { number: '450+', label: 'Confessions Posted' },
            { number: '1500+', label: 'Anonymous Chats Created' },
          ].map((stat, index) => (
            <motion.div
              key={index}
              className="text-center"
              whileHover={{ scale: 1.1 }}
              transition={{ duration: 0.3 }}
            >
              <div className="text-4xl md:text-5xl font-medium tracking-tight text-sp-cyan mb-2">
                {stat.number}
              </div>
              <div className="text-sp-text-secondary font-medium">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div> */}
      </div>
    </section>
  );
};
export default CommunitySection;



